import React, { createContext, useContext, useState, useEffect } from 'react';
import { useStytchB2BClient, useStytchMemberSession } from '@stytch/react/b2b';
import { useNavigate, useLocation } from 'react-router-dom';
import { SessionTokens } from "@stytch/vanilla-js/b2b";

interface AuthContextType {
    sessionToken: string | null;
    setSessionToken: (token: string | null) => void;
}

const AuthContext = createContext<AuthContextType>({
    sessionToken: null,
    setSessionToken: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [sessionToken, setSessionToken] = useState<string | null>(null);
    const { session, fromCache } = useStytchMemberSession();
    const stytch = useStytchB2BClient();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {

        const publicRoutes = ["/auth", "/", "/LandingPage", "/Contact", "/LandingPage.html"];

        // Redirect to previous location after login
        if (session && location.pathname === '/authentication/sign-in') {
            const from = location.state?.from?.pathname || "/";
            navigate(from, { replace: true });
        }

        // Automatically handle session expiry
        if (!session && !publicRoutes.includes(location.pathname)) {
            console.warn("→ Redirecting to /auth due to missing session and protected route");
            setSessionToken(null);
            navigate("/auth");
        }

        // Fetch session token if needed
        const fetchSessionToken = async () => {
            if (!fromCache) {
                try {
                    const tokens: SessionTokens = stytch.session.getTokens();
                    setSessionToken(tokens.session_token);
                } catch (error) {
                    console.error("→ Error retrieving session token:", error);
                    setSessionToken(null);
                }
            }
        };

        fetchSessionToken();
    }, [session, fromCache, navigate, location, stytch]);

    return (
        <AuthContext.Provider value={{ sessionToken, setSessionToken }}>
            {children}
        </AuthContext.Provider>
    );
};
